type Status = "not_started" | "in_progress" | "done" | "stuck";

export const statusStyle: Record<Status, string> = {
  not_started: "bg-slate-50 border-slate-300 text-slate-800",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  done: "bg-emerald-50 border-emerald-300 text-emerald-800",
  stuck: "bg-rose-50 border-rose-300 text-rose-800",
};

export const statusDot: Record<Status, string> = {
  not_started: "bg-slate-400",
  in_progress: "bg-amber-500",
  done: "bg-emerald-600",
  stuck: "bg-rose-500",
};

export function SegmentedBar({
  total,
  ...counts
}: Record<Status, number> & { total: number }) {
  const w = (n: number) => (total ? `${(n / total) * 100}%` : "0%");
  return (
    <div className="h-2 w-full rounded-full overflow-hidden bg-slate-200 flex">
      <div
        className="h-full"
        style={{
          width: w(counts.done),
          backgroundColor: "var(--color-emerald-500)",
        }}
      />
      <div
        className="h-full"
        style={{
          width: w(counts.in_progress),
          backgroundColor: "var(--color-amber-500)",
        }}
      />
      <div
        className="h-full"
        style={{
          width: w(counts.stuck),
          backgroundColor: "var(--color-rose-500)",
        }}
      />
      <div
        className="h-full"
        style={{
          width: w(counts.not_started),
          backgroundColor: "var(--color-slate-400)",
        }}
      />
    </div>
  );
}

export function RoadmapSkeleton() {
  const ProgressRingSkeleton = () => (
    <div className="relative">
      <div className="h-12 w-12 rounded-full border-3 border-slate-200" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-4 w-8 bg-slate-300 rounded" />
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <div className="h-8 w-64 bg-slate-300 rounded-lg mb-2" />
            <div className="h-4 w-48 bg-slate-300 rounded" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-20 bg-slate-300 rounded-lg" />
            <div className="h-9 w-20 bg-slate-300 rounded-lg" />
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-r from-blue-50 to-slate-50 border border-slate-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="h-7 w-56 bg-slate-300 rounded-lg" />
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-slate-300 border border-slate-300" />
                  <div>
                    <div className="h-7 w-16 bg-slate-300 rounded-lg mb-1" />
                    <div className="h-3 w-20 bg-slate-300 rounded" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-slate-300 border border-slate-300" />
                  <div>
                    <div className="h-7 w-24 bg-slate-300 rounded-lg mb-1" />
                    <div className="h-3 w-28 bg-slate-300 rounded" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <ProgressRingSkeleton />
                <div className="h-3 w-20 bg-slate-300 rounded mt-2 mx-auto" />
              </div>
              <div className="h-12 w-px bg-slate-300 hidden lg:block" />
              <div className="grid grid-cols-2 gap-3 min-w-[200px]">
                <div className="text-center">
                  <div className="h-7 w-12 bg-slate-300 rounded-lg mx-auto mb-1" />
                  <div className="h-3 w-16 bg-slate-300 rounded mx-auto" />
                </div>
                <div className="text-center">
                  <div className="h-7 w-12 bg-slate-300 rounded-lg mx-auto mb-1" />
                  <div className="h-3 w-16 bg-slate-300 rounded mx-auto" />
                </div>
                <div className="text-center">
                  <div className="h-7 w-12 bg-slate-300 rounded-lg mx-auto mb-1" />
                  <div className="h-3 w-10 bg-slate-300 rounded mx-auto" />
                </div>
                <div className="text-center">
                  <div className="h-7 w-12 bg-slate-300 rounded-lg mx-auto mb-1" />
                  <div className="h-3 w-20 bg-slate-300 rounded mx-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-4 z-10 mb-6">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-100" />
              <div className="space-y-2">
                <div className="h-4 w-40 bg-blue-200 rounded" />
                <div className="h-3 w-32 bg-blue-200 rounded" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-9 w-20 bg-blue-200 rounded-lg" />
              <div className="h-9 w-36 bg-blue-300 rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="h-5 w-20 bg-slate-300 rounded-lg" />
                <div className="h-4 w-12 bg-slate-300 rounded" />
              </div>
              <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-200" />
                        <div className="space-y-1">
                          <div className="h-4 w-32 bg-slate-300 rounded" />
                          <div className="h-3 w-24 bg-slate-200 rounded" />
                        </div>
                      </div>
                      <div className="h-5 w-12 bg-slate-200 rounded" />
                    </div>
                    <div className="mt-3">
                      <div className="h-1.5 w-full bg-slate-200 rounded-full">
                        <div
                          className="h-full bg-slate-300 rounded-full"
                          style={{ width: `${Math.random() * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="h-5 w-24 bg-slate-300 rounded-lg mb-3" />
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-slate-300" />
                      <div className="h-3 w-20 bg-slate-200 rounded" />
                    </div>
                    <div className="h-4 w-6 bg-slate-300 rounded" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="space-y-1">
                <div className="h-6 w-48 bg-slate-300 rounded-lg" />
                <div className="h-4 w-32 bg-slate-200 rounded" />
              </div>
              <div className="h-9 w-20 bg-slate-300 rounded-lg" />
            </div>

            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg border border-slate-200 p-3"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="h-7 w-28 bg-slate-300 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="h-5 flex-1 bg-slate-300 rounded" />
                        <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                      </div>
                    </div>
                    <div className="h-4 w-4 bg-slate-300 rounded" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                    <div className="h-8 bg-slate-200 rounded-md" />
                    <div className="h-8 bg-slate-200 rounded-md" />
                    <div className="h-8 bg-slate-200 rounded-md" />
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                    <div>
                      <div className="h-3 w-24 bg-slate-300 rounded mb-1.5" />
                      <div className="h-16 bg-slate-100 rounded-lg" />
                    </div>
                    <div>
                      <div className="h-3 w-28 bg-slate-300 rounded mb-1.5" />
                      <div className="h-12 bg-slate-100 rounded-lg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden">
            <div className="text-center py-12">
              <div className="mx-auto max-w-xs">
                <div className="rounded-full bg-slate-100 p-4 w-fit mx-auto">
                  <div className="h-8 w-8 bg-slate-300 rounded" />
                </div>
                <div className="mt-4 h-5 w-32 bg-slate-300 rounded-lg mx-auto" />
                <div className="mt-2 h-4 w-48 bg-slate-200 rounded mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
